// deps
import { useReducer, useEffect, useMemo, useContext, useCallback, useRef } from "react";

// reducers
import { GetPayLaterLinkActionTypes, getPayLaterLinkReducer } from "@payment-client/reducers/PayLater";

// hooks
import { useMountedRef, captureError } from "@swiggy-private/core-ui";

// constants
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import {
    ApiContext,
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
    CheckoutContext,
    ConfigContext,
    PublicApiContext,
} from "@swiggy-private/checkout-core";
import { Ok } from "@swiggy-private/js-fp";

// helpers
import {
    defaultReducerStateBuilder,
    dispatchPayLaterLinkResponse,
    fetchPaylaterLinkStatus,
    getAllPaylaterMethods,
    CheckPayLaterLinkResponse,
} from "@payment-client/helpers/PaylaterHelper";

// contexts
import { PaylaterLinkedContextValue } from "@payment-client/contexts/PaylaterLinkedContext";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";

export function usePayLaterLinkedStatus(paymentResponse: GetPaymentMethodsResponse): PaylaterLinkedContextValue {
    const isMountedRef = useMountedRef();
    const { host } = useContext(ApiContext);
    const { checkoutState } = useContext(CheckoutContext);
    const apiHost = useMemo(() => host.unwrap(), [host]);
    const paymentMethods = useMemo(() => getAllPaylaterMethods(paymentResponse.paymentGroup), [paymentResponse]);
    const { cachePaymentData } = useContext(ConfigContext);
    const apiCallRef = useRef(false);
    const cacheRef = useRef<Map<string, CheckPayLaterLinkResponse>>(new Map());
    const canCachePaylaterData = cachePaymentData?.unwrapOr(true);

    const [reducerState, dispatch] = useReducer(getPayLaterLinkReducer, paymentMethods, defaultReducerStateBuilder);

    const publicApiConfig = useContext(PublicApiContext);

    const refetchLinkStatus = useCallback(
        async (name: string): Promise<void> => {
            const method = paymentMethods.find((method) => method.name === name);
            if (!method) {
                return;
            }

            dispatch({ type: GetPayLaterLinkActionTypes.REQUEST, name });
            let response: CheckPayLaterLinkResponse;

            const isPublicApiEnable = isPublicApiEnabled(
                publicApiConfig || {},
                CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.PAYLATER_APIS,
                method.paymentCode,
            );

            if (method.enabled) {
                response = await fetchPaylaterLinkStatus(
                    apiHost,
                    {
                        name: method.name,
                        paymentCode: method.paymentCode,
                        payableAmount: method.payableAmount,
                        meta: method.meta,
                    },
                    undefined,
                    isPublicApiEnable,
                );
            } else {
                response = {
                    name: method.name,
                    response: Ok({
                        linked: false,
                    }),
                };
            }
            if (isMountedRef.current) {
                // Keep cache consistent with manual refetches so subsequent remounts/initial-load
                // don't read stale cached entries and overwrite fresh state
                if (canCachePaylaterData) {
                    cacheRef.current.set(response.name, response);
                }
                dispatchPayLaterLinkResponse(response, dispatch);
            }
        },
        [apiHost, isMountedRef, paymentMethods, publicApiConfig, canCachePaylaterData],
    );

    useEffect(() => {
        if (apiCallRef.current) {
            return;
        }
        apiCallRef.current = true;
        paymentMethods
            .map((paymentMethod) => {
                const isPublicApiEnable = isPublicApiEnabled(
                    publicApiConfig || {},
                    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.PAYLATER_APIS,
                    paymentMethod.paymentCode,
                );

                // Use cached data if available for this payment method
                const cached = cacheRef.current.get(paymentMethod.name);
                if (cached) {
                    return Promise.resolve(cached);
                }

                return fetchPaylaterLinkStatus(apiHost, paymentMethod, checkoutState?.type, isPublicApiEnable);
            })
            .forEach((fetchPromise) => {
                fetchPromise
                    .then((response) => {
                        if (isMountedRef.current) {
                            if (canCachePaylaterData) {
                                cacheRef.current.set(response.name, response);
                            }
                            dispatchPayLaterLinkResponse(response, dispatch);
                        }
                    })
                    .catch(captureError);
            });
    }, [isMountedRef, apiHost, paymentMethods, checkoutState?.type, publicApiConfig, canCachePaylaterData]);

    return {
        paylaterState: reducerState,
        setPaylaterState: dispatch,
        refetchPaylaterLinkStatus: refetchLinkStatus,
    };
}
